// src/components/admin/ProductMultiSpecImportDialog.jsx
import React, { useState } from 'react';
import { X, Upload, CheckCircle2, AlertCircle, Package } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../../context/AppContext';
import { productApi } from '../../api';

// 列位置对应「多规格商品批量导入模板.xlsx」（37列，商品级20列 + SKU级17列）
const COL = {
  groupId: 0, name: 1, name_en: 2, name_km: 3, barcode: 4,
  parentCategoryName: 5, childCategoryName: 6, unitName: 7,
  cost_price: 8, sale_price: 9, vip_price: 10,
  box_quantity: 11, box_price: 12, vip_box_price: 13,
  min_stock: 14, image: 15, sort_order: 16,
  show_in_pos: 17, show_in_h5: 18, show_in_transfer: 19,
  spec1TypeZh: 20, spec1TypeEn: 21, spec1TypeKm: 22,
  spec1ValZh: 23, spec1ValEn: 24, spec1ValKm: 25,
  spec2TypeZh: 26, spec2TypeEn: 27, spec2TypeKm: 28,
  spec2ValZh: 29, spec2ValEn: 30, spec2ValKm: 31,
  skuPrice: 32, skuVipPrice: 33, skuStock: 34, skuCode: 35, skuImage: 36,
};

const ProductMultiSpecImportDialog = ({ onClose, onImported }) => {
  const { language } = useApp();
  const [fileName, setFileName] = useState('');
  const [groups, setGroups] = useState([]); // 归组后的商品数组
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const T = {
    title: language === 'en' ? 'Batch Import Multi-Spec Products' : language === 'km' ? 'នាំចូលទំនិញច្រើនជម្រើស' : '批量导入多规格商品',
    choosePlaceholder: language === 'en' ? 'Click to choose an Excel file (.xlsx)' : language === 'km' ? 'ចុចដើម្បីជ្រើសរើសឯកសារ Excel (.xlsx)' : '点击选择Excel文件（.xlsx）',
    previewLabel: (n) => language === 'en' ? `Preview (${n} products) — check before confirming`
      : language === 'km' ? `មើលជាមុន (ទំនិញ ${n}) — សូមពិនិត្យមុនបញ្ជាក់`
      : `预览（共 ${n} 个商品，确认无误再点下方"确认导入"）`,
    skuCount: (n) => language === 'en' ? `${n} SKUs` : language === 'km' ? `SKU ចំនួន ${n}` : `${n} 个规格`,
    statCreated: language === 'en' ? 'Created' : language === 'km' ? 'បានបង្កើត' : '成功',
    statFailed: language === 'en' ? 'Failed' : language === 'km' ? 'បរាជ័យ' : '失败',
    failedDetail: language === 'en' ? 'Failure details' : language === 'km' ? 'ព័ត៌មានលម្អិតបញ្ហា' : '失败详情',
    createdList: language === 'en' ? 'Newly created products' : language === 'km' ? 'ទំនិញដែលបានបង្កើតថ្មី' : '新建的商品',
    cancel: language === 'en' ? 'Cancel' : language === 'km' ? 'បោះបង់' : '取消',
    confirmImport: (n) => language === 'en' ? `Confirm Import (${n})` : language === 'km' ? `បញ្ជាក់នាំចូល (${n})` : `确认导入（${n}个商品）`,
    importing: language === 'en' ? 'Importing...' : language === 'km' ? 'កំពុងនាំចូល...' : '导入中...',
    done: language === 'en' ? 'Done' : language === 'km' ? 'រួចរាល់' : '完成',
    noDataError: language === 'en'
      ? 'No importable data found — check that example rows were removed and real data starts from row 4.'
      : language === 'km'
      ? 'រកមិនឃើញទិន្នន័យសម្រាប់នាំចូល — សូមពិនិត្យថាបានលុបជួរឧទាហរណ៍ ហើយទិន្នន័យពិតចាប់ផ្តើមពីជួរទី៤។'
      : '没有读到可导入的数据，检查一下是不是把示例行也删了、数据没有从第4行开始填。',
    groupIdMissing: language === 'en' ? 'has a row missing the group ID' : language === 'km' ? 'មានជួរដែលគ្មានលេខក្រុម' : '有一行缺少分组编号',
    parseError: (msg) => language === 'en' ? `File parsing failed, make sure it's a standard .xlsx file: ${msg}`
      : language === 'km' ? `ការញែកឯកសារបរាជ័យ សូមប្រាកដថាជាឯកសារ .xlsx ស្តង់ដារ៖ ${msg}`
      : '文件解析失败，确认一下是不是标准的.xlsx文件：' + msg,
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        // 第1行是表头，之后每一行只要"前37列（真正的数据列）"有任何内容就算数据行——
        // 之前这里判断"这一行有没有数据"是看整行(包括最右边的填表说明那一列)有没有内容，
        // 但说明文字那一列本身跨了十几行，导致光有说明文字、数据列全是空的那些行
        // 被误判成"数据行"，再去查它的分组编号——自然是空的，报错"缺少分组编号"。
        // 现在只检查前37列（数据列），完全不看说明列，同时也不再假设"前面一定有2行示例"，
        // 不管示例行是保留还是被删掉，都能正确处理。
        const DATA_COL_COUNT = 37;
        const rawRows = raw.slice(1).filter(row =>
          row.slice(0, DATA_COL_COUNT).some(cell => String(cell).trim() !== '')
        );

        if (rawRows.length === 0) {
          setParseError(T.noDataError);
          return;
        }

        // 按分组编号归组
        const groupMap = new Map(); // groupId -> group对象
        const order = []; // 保持出现顺序

        for (const row of rawRows) {
          const groupId = String(row[COL.groupId] || '').trim();
          if (!groupId) {
            setParseError(T.groupIdMissing);
            return;
          }

          if (!groupMap.has(groupId)) {
            groupMap.set(groupId, {
              groupId,
              name: String(row[COL.name] || '').trim(),
              name_en: String(row[COL.name_en] || '').trim(),
              name_km: String(row[COL.name_km] || '').trim(),
              barcode: String(row[COL.barcode] || '').trim(),
              parentCategoryName: String(row[COL.parentCategoryName] || '').trim(),
              childCategoryName: String(row[COL.childCategoryName] || '').trim(),
              unitName: String(row[COL.unitName] || '').trim(),
              cost_price: row[COL.cost_price],
              sale_price: row[COL.sale_price],
              vip_price: row[COL.vip_price],
              box_quantity: row[COL.box_quantity],
              box_price: row[COL.box_price],
              vip_box_price: row[COL.vip_box_price],
              min_stock: row[COL.min_stock],
              image: String(row[COL.image] || '').trim(),
              sort_order: row[COL.sort_order],
              show_in_pos: String(row[COL.show_in_pos] || '是').trim() !== '否',
              show_in_h5: String(row[COL.show_in_h5] || '是').trim() !== '否',
              show_in_transfer: String(row[COL.show_in_transfer] || '是').trim() !== '否',
              specTypes: [], // [{name, name_en, name_km}]，最多2个，按首次出现顺序收集
              skus: []
            });
            order.push(groupId);
          }
          const group = groupMap.get(groupId);

          // 收集规格类型（同组内只记一次，取第一次出现的翻译）
          const spec1TypeName = String(row[COL.spec1TypeZh] || '').trim();
          const spec2TypeName = String(row[COL.spec2TypeZh] || '').trim();
          if (spec1TypeName && !group.specTypes[0]) {
            group.specTypes[0] = {
              name: spec1TypeName,
              name_en: String(row[COL.spec1TypeEn] || '').trim(),
              name_km: String(row[COL.spec1TypeKm] || '').trim()
            };
          }
          if (spec2TypeName && !group.specTypes[1]) {
            group.specTypes[1] = {
              name: spec2TypeName,
              name_en: String(row[COL.spec2TypeEn] || '').trim(),
              name_km: String(row[COL.spec2TypeKm] || '').trim()
            };
          }

          const values = [];
          const spec1Val = String(row[COL.spec1ValZh] || '').trim();
          if (spec1Val) {
            values.push({
              value: spec1Val,
              value_en: String(row[COL.spec1ValEn] || '').trim(),
              value_km: String(row[COL.spec1ValKm] || '').trim()
            });
          }
          const spec2Val = String(row[COL.spec2ValZh] || '').trim();
          if (spec2Val) {
            values.push({
              value: spec2Val,
              value_en: String(row[COL.spec2ValEn] || '').trim(),
              value_km: String(row[COL.spec2ValKm] || '').trim()
            });
          }

          group.skus.push({
            values,
            price: row[COL.skuPrice],
            vip_price: row[COL.skuVipPrice],
            stock: row[COL.skuStock],
            sku_code: String(row[COL.skuCode] || '').trim(),
            image: String(row[COL.skuImage] || '').trim()
          });
        }

        setGroups(order.map(id => groupMap.get(id)));
      } catch (err) {
        setParseError(T.parseError(err.message));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (groups.length === 0) return;
    setSubmitting(true);
    try {
      const res = await productApi.bulkImportMultiSpec({ groups });
      if (res.success) {
        setResult(res.data);
        onImported();
      } else {
        setParseError(res.message || T.parseError(''));
      }
    } catch (err) {
      setParseError(err.message || T.parseError(''));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="border-b p-6 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold">{T.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {!result && (
            <div>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{fileName || T.choosePlaceholder}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
              </label>
              {parseError && <p className="text-sm text-red-600 mt-2">{parseError}</p>}
            </div>
          )}

          {groups.length > 0 && !result && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{T.previewLabel(groups.length)}</p>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {groups.map((g, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-blue-500" />
                        <span className="font-medium text-sm">{g.name || <span className="text-red-500">(未填名称)</span>}</span>
                        <span className="text-xs text-gray-400">[{g.groupId}]</span>
                      </div>
                      <span className="text-xs text-gray-500">{T.skuCount(g.skus.length)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.skus.map((sku, j) => (
                        <span key={j} className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1">
                          {sku.values.map(v => v.value).join(' / ') || '(无规格值)'} · {sku.stock || 0}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{result.created.length}</p>
                  <p className="text-xs text-green-700 mt-1">{T.statCreated}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{result.failed.length}</p>
                  <p className="text-xs text-red-700 mt-1">{T.statFailed}</p>
                </div>
              </div>

              {result.failed.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />{T.failedDetail}
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.failed.map((f, i) => (
                      <div key={i} className="text-xs bg-red-50 rounded px-3 py-2 text-red-700">
                        [{f.groupId}] 「{f.name || '(未填名称)'}」：{f.reasons.join('；')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.created.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />{T.createdList}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.created.map((c, i) => (
                      <span key={i} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
                        {c.name}（{c.skuCount}）
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t p-6 flex justify-end space-x-3 flex-shrink-0">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                {T.cancel}
              </button>
              <button
                onClick={handleSubmit}
                disabled={groups.length === 0 || submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? T.importing : T.confirmImport(groups.length)}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {T.done}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductMultiSpecImportDialog;